
/**
 * Chandelier Area Series Plugin for Lightweight Charts v5
 * 
 * Renders a filled area between the Chandelier Exit Stop value and the Candle Midpoint.
 * - Uptrend (Direction 1): Green Area from Long Stop to (Open+Close)/2
 * - Downtrend (Direction -1): Red Area from Short Stop to (Open+Close)/2
 */

class ChandelierAreaRenderer {
    constructor() {
        this._data = null;
        this._options = null;
        
        // Bind methods to ensure they exist on valid key checks
        this.isWhitespace = this.isWhitespace.bind(this);
    }

    // Required by some v5 versions for data validation
    isWhitespace(data) {
        return !data || !data.value;
    }
    
    // Optional interface methods
    destroy() {}

    draw(target, priceToCoordinate) {
        target.useBitmapCoordinateSpace((scope) => {
            //! [DIAG] One-time-per-burst snapshot of plugin input: tells us if live
            //! bars reached the renderer with the expected shape, vs dropped upstream.
            if (typeof window !== 'undefined') {
                window.__diagChandelier = (window.__diagChandelier || 0) + 1;
                if (window.__diagChandelier <= 3 || window.__diagChandelier % 60 === 0) {
                    const dtype = this._data === null ? 'null' :
                        (Array.isArray(this._data) ? 'array' : typeof this._data);
                    const barsLen = this._data && Array.isArray(this._data.bars) ? this._data.bars.length : 'n/a';
                    const vr = this._data && this._data.visibleRange
                        ? `${this._data.visibleRange.from}..${this._data.visibleRange.to}` : 'null';
                    let sampleFirst = 'n/a', sampleLast = 'n/a';
                    if (this._data && Array.isArray(this._data.bars) && this._data.bars.length > 0) {
                        const b0 = this._data.bars[0];
                        const bN = this._data.bars[this._data.bars.length - 1];
                        sampleFirst = `t=${b0?.time} v=${JSON.stringify(b0?.originalData?.value)} o=${b0?.originalData?.open} c=${b0?.originalData?.close} x=${b0?.x}`;
                        sampleLast = `t=${bN?.time} v=${JSON.stringify(bN?.originalData?.value)} o=${bN?.originalData?.open} c=${bN?.originalData?.close} x=${bN?.x}`;
                    }
                    console.log(`[DIAG] chandelier.draw #${window.__diagChandelier} dataType=${dtype} bars=${barsLen} vr=${vr}`);
                    console.log(`[DIAG] chandelier.draw first: ${sampleFirst}`);
                    console.log(`[DIAG] chandelier.draw last:  ${sampleLast}`);
                }
            }

            //! Guard: _data must be the lw-charts {bars, visibleRange} structure. A raw
            //! array here means a stale direct update() call overwrote it — skip the
            //! frame rather than throwing TypeError on .bars.length (which makes lw-charts
            //! stop calling draw() entirely for this series).
            if (this._data === null || Array.isArray(this._data) ||
                !this._data.bars || this._data.bars.length === 0 || this._data.visibleRange === null) {
                return;
            }

            const ctx = scope.context;
            const bars = this._data.bars;
            
            // Define options and styles
            const options = this._options || {};
            const upColor = options.upColor || 'rgba(76, 175, 80, 0.2)'; // Green
            const downColor = options.downColor || 'rgba(239, 83, 80, 0.2)'; // Red
            const upLineColor = options.upLineColor || 'rgba(76, 175, 80, 1)';
            const downLineColor = options.downLineColor || 'rgba(239, 83, 80, 1)';
            const lineWidth = options.lineWidth || 2;
            const lineStyle = options.lineStyle || 0;
            const lineType = options.lineType || 0; // 0: Simple, 1: Step

            ctx.lineWidth = lineWidth * scope.verticalPixelRatio;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';

            const setLineDashStyle = () => {
                ctx.setLineDash([]);
                const pixelRatio = scope.verticalPixelRatio;
                if (lineStyle === 1) ctx.setLineDash([lineWidth * pixelRatio, lineWidth * pixelRatio]); // Dotted
                else if (lineStyle === 2) ctx.setLineDash([lineWidth * 4 * pixelRatio, lineWidth * 2 * pixelRatio]); // Dashed
                else if (lineStyle === 3) ctx.setLineDash([lineWidth * 8 * pixelRatio, lineWidth * 4 * pixelRatio]); // LargeDashed
            };

            // Helper to compute point coordinates using the priceToCoordinate converter
            const getPoint = (index) => {
                const bar = bars[index];
                if (!bar || !bar.originalData || !bar.originalData.value) return null;

                // Check for valid X coordinate provided by library
                if (bar.x === undefined || isNaN(bar.x)) return null;

                const { direction, long_stop, short_stop } = bar.originalData.value;
                const { open, close } = bar.originalData;

                // Use the stop level based on direction
                const stopPrice = direction === 1 ? parseFloat(long_stop) : parseFloat(short_stop);
                const midPrice = (parseFloat(open) + parseFloat(close)) / 2;

                // priceToCoordinate returns null when the price is outside the
                // visible price range (e.g. during zoom). Skip these points —
                // falling back to 0 drew to the canvas top, creating fill artifacts.
                const yStop = priceToCoordinate(stopPrice);
                const yMid = priceToCoordinate(midPrice);
                if (yStop === null || yMid === null) return null;

                //* Convert media (CSS) pixels to bitmap (device) pixels on BOTH axes.
                //* priceToCoordinate returns media Y; inside useBitmapCoordinateSpace the
                //* canvas is device-pixel sized, so an unscaled Y compresses the whole
                //* overlay toward the top of the pane on any devicePixelRatio != 1
                //* (board #16 — overlay drifted off the candles on a scaled display).
                const xBitmap = bar.x * scope.horizontalPixelRatio;

                return {
                    x: xBitmap,
                    yStop: yStop * scope.verticalPixelRatio,
                    yMid: yMid * scope.verticalPixelRatio,
                    colorType: direction === 1 ? 1 : -1
                };
            };

            // Render only the visible range — no from-1 extension.
            // Extending before the visible range caused edge artifacts when
            // the off-screen bar had a price gap (overnight, session boundary).
            const from = this._data.visibleRange.from;
            const to = Math.min(bars.length - 1, this._data.visibleRange.to);

            // Group bars by trend (Direction)
            const segments = [];
            let currentSegment = null;

            //! [DIAG] Tally reasons getPoint returned null across this draw.
            const skipReasons = { noValue: 0, noX: 0, badStop: 0, badMid: 0, coord: 0 };
            let firstSkippedBar = null;

            for (let i = from; i <= to; i++) {
                const bar = bars[i];
                const point = getPoint(i);
                if (!point) {
                    if (!firstSkippedBar) firstSkippedBar = bar;
                    if (!bar || !bar.originalData || !bar.originalData.value) skipReasons.noValue++;
                    else if (bar.x === undefined || isNaN(bar.x)) skipReasons.noX++;
                    else {
                        const v = bar.originalData.value;
                        const stop = v.direction === 1 ? parseFloat(v.long_stop) : parseFloat(v.short_stop);
                        const mid = (parseFloat(bar.originalData.open) + parseFloat(bar.originalData.close)) / 2;
                        if (isNaN(stop)) skipReasons.badStop++;
                        else if (isNaN(mid)) skipReasons.badMid++;
                        else skipReasons.coord++;
                    }
                    currentSegment = null;
                    continue;
                }

                if (!currentSegment || currentSegment.type !== point.colorType) {
                    currentSegment = {
                        type: point.colorType,
                        points: [point]
                    };
                    segments.push(currentSegment);
                } else {
                    currentSegment.points.push(point);
                }
            }

            //! [DIAG] Report skips + segment count once the iteration is done.
            if (typeof window !== 'undefined' && (window.__diagChandelier <= 3 || window.__diagChandelier % 60 === 0)) {
                const skipTotal = skipReasons.noValue + skipReasons.noX + skipReasons.badStop + skipReasons.badMid + skipReasons.coord;
                const fsb = firstSkippedBar
                    ? `t=${firstSkippedBar.time} v=${JSON.stringify(firstSkippedBar.originalData?.value)} o=${firstSkippedBar.originalData?.open} c=${firstSkippedBar.originalData?.close} x=${firstSkippedBar.x}`
                    : 'none';
                console.log(`[DIAG] chandelier.draw segments=${segments.length} skipTotal=${skipTotal} reasons=${JSON.stringify(skipReasons)}`);
                console.log(`[DIAG] chandelier.draw firstSkippedBar: ${fsb}`);
            }

            // Draw Segments
            segments.forEach(seg => {
                if (seg.points.length < 2) return;

                // 1. Draw Area
                ctx.fillStyle = seg.type === 1 ? upColor : downColor;
                ctx.beginPath();
                ctx.moveTo(seg.points[0].x, seg.points[0].yStop);
                
                for (let i = 1; i < seg.points.length; i++) {
                    const p = seg.points[i];
                    const prev = seg.points[i-1];
                    if (lineType === 1) ctx.lineTo(p.x, prev.yStop); // Step
                    ctx.lineTo(p.x, p.yStop);
                }
                // Close area path by going back through midpoints
                for (let i = seg.points.length - 1; i >= 0; i--) {
                    ctx.lineTo(seg.points[i].x, seg.points[i].yMid);
                }
                ctx.closePath();
                ctx.fill();

                // 2. Draw Stop Line
                ctx.strokeStyle = seg.type === 1 ? upLineColor : downLineColor;
                ctx.lineWidth = lineWidth * scope.verticalPixelRatio;
                setLineDashStyle();
                
                ctx.beginPath();
                ctx.moveTo(seg.points[0].x, seg.points[0].yStop);
                for (let i = 1; i < seg.points.length; i++) {
                    const p = seg.points[i];
                    const prev = seg.points[i-1];
                    if (lineType === 1) ctx.lineTo(p.x, prev.yStop); // Step
                    ctx.lineTo(p.x, p.yStop);
                }
                ctx.stroke();
                ctx.setLineDash([]); // Reset
            });
        });
    }

    update(data, options) {
        this._data = data;
        this._options = options;
    }
}

export class ChandelierAreaSeries {
    constructor() {
        this._renderer = new ChandelierAreaRenderer();
        
        // Bind all interface methods
        this.renderer = this.renderer.bind(this);
        this.update = this.update.bind(this);
        this.isWhitespace = this.isWhitespace.bind(this);
        this.priceValueBuilder = this.priceValueBuilder.bind(this);
        this.defaultOptions = this.defaultOptions.bind(this);
        this.autoscaleInfo = this.autoscaleInfo.bind(this);
    }

    // Default options (called during series creation)
    defaultOptions() {
        return {
            upColor: 'rgba(76, 175, 80, 0.2)',
            downColor: 'rgba(239, 83, 80, 0.2)',
            upLineColor: '#4CAF50',
            downLineColor: '#F44336',
            lineWidth: 2,
            lineStyle: 0,
            lineType: 0,
        };
    }

    renderer() {
        return this._renderer;
    }

    update(data, options) {
        this._renderer.update(data, options);
    }
    
    isWhitespace(data) {
        return !data || !data.value;
    }
    
    // Required by v5 for auto-scaling
    priceValueBuilder(data) {
        if (!data || !data.value) return [NaN, NaN, NaN];
        
        // Helper to ensure valid price number (ignores 0)
        const val = (v) => {
            if (typeof v === 'number') {
                return v === 0 ? NaN : v;
            }
            if (typeof v === 'string') {
                const f = parseFloat(v);
                return (isNaN(f) || f === 0) ? NaN : f;
            }
            return NaN;
        };

        const longStop = val(data.value.long_stop);
        const shortStop = val(data.value.short_stop);
        const open = val(data.open);
        const close = val(data.close);
        const midpoint = (!isNaN(open) && !isNaN(close)) ? (open + close) / 2 : NaN;

        return [ longStop, shortStop, midpoint ];
    }

    //-- Let lightweight-charts derive the scale from priceValueBuilder values
    //   (long_stop, short_stop, midpoint). A hardcoded 0–100k range caused a
    //   first-render bleed artifact by distorting priceToCoordinate on the
    //   initial auto-scale pass.
    autoscaleInfo(startTimePoint, endTimePoint) {
        return null;
    }

    destroy() {}
}
